import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, UserX } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';

interface UserBlockingModalProps {
  children: React.ReactNode;
}

export const UserBlockingModal: React.FC<UserBlockingModalProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [blockedUsers, setBlockedUsers] = useState<any[]>([]);
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;

  const handleBlockUser = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a username or email to block');
      return;
    }

    try {
      // TODO: Implement actual user blocking logic
      toast.success(`User "${searchTerm}" has been blocked`);
      setSearchTerm('');
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const handleUnblockUser = async (userId: string) => {
    try {
      // TODO: Implement actual user unblocking logic
      toast.success('User has been unblocked');
      setBlockedUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      toast.error('Failed to unblock user');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserX className="h-5 w-5 text-orange-400" />
            Manage Blocked Users
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Block New User */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Block a User</h3>
            <div className="space-y-2">
              <Label htmlFor="search-user">Username or Email</Label>
              <div className="flex gap-2">
                <Input
                  id="search-user"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Enter username or email..."
                  className="bg-gray-800 border-gray-600 text-white"
                />
                <Button
                  onClick={handleBlockUser}
                  variant="destructive"
                  className="px-3"
                >
                  Block
                </Button>
              </div>
            </div>
          </div>

          {/* Currently Blocked Users */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Currently Blocked</h3>
            {blockedUsers.length === 0 ? (
              <p className="text-gray-400 text-sm">No users are currently blocked</p>
            ) : (
              <div className="space-y-2">
                {blockedUsers.map((blockedUser) => (
                  <div key={blockedUser.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                    <span className="text-white">{blockedUser.username || blockedUser.email}</span>
                    <Button
                      onClick={() => handleUnblockUser(blockedUser.id)}
                      variant="outline"
                      size="sm"
                      className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      Unblock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};